import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';

const MessagesPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [draft, setDraft] = useState('');
  const [unreadByUserId, setUnreadByUserId] = useState({});
  const [currentVetId, setCurrentVetId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { refreshUnreadCount } = useUnreadMessages();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUsers();
      fetchUnreadCounts();
    }
  }, [user]);

  useEffect(() => {
    if (user && selectedUserId) {
      fetchConversation();
    }
  }, [user, selectedUserId]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!currentVetId) return;

    const channel = supabase
      .channel('messages-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_vet_id=eq.${currentVetId}`,
        },
        () => {
          fetchConversation();
          fetchUnreadCounts();
          refreshUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentVetId, selectedUserId]);

  const init = async () => {
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) { navigate('/login'); return; }
      setUser(u);
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('vets')
        .select('id, first_name, last_name, user_id, email')
        .eq('deleted', false);

      if (error) throw error;

      // Find current user's vet record
      const currentVet = (data || []).find(v => v.user_id === user.id);
      if (currentVet) setCurrentVetId(currentVet.id);

      // Filter out current user
      const otherVets = (data || []).filter(v => v.user_id !== user.id);
      setUsers(otherVets);

      // Auto-select first user
      if (!selectedUserId && otherVets.length > 0) {
        setSelectedUserId(otherVets[0].id);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setUsers([]);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      // Get current user's vet record
      const { data: currentVet, error: vetError } = await supabase
        .from('vets')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (vetError || !currentVet) {
        console.error('Current user vet not found:', vetError);
        setUnreadByUserId({});
        return;
      }
      
      const { data, error } = await supabase
        .from('messages')
        .select('sender_vet_id, read_at')
        .eq('recipient_vet_id', currentVet.id)
        .is('read_at', null);
      if (error) throw error;
      const counts = {};
      (data || []).forEach(m => {
        counts[m.sender_vet_id] = (counts[m.sender_vet_id] || 0) + 1;
      });
      setUnreadByUserId(counts);
    } catch (err) {
      console.error('Failed to compute unread counts:', err);
      setUnreadByUserId({});
    }
  };

  const fetchConversation = async () => {
    try {
      // Get current user's vet record
      const { data: currentVet, error: vetError } = await supabase
        .from('vets')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (vetError || !currentVet) {
        console.error('Current user vet not found:', vetError);
        setMessages([]);
        return;
      }

      // Fetch conversation between vets
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_vet_id.eq.${currentVet.id},recipient_vet_id.eq.${selectedUserId}),and(sender_vet_id.eq.${selectedUserId},recipient_vet_id.eq.${currentVet.id})`)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setMessages(data || []);

      // Mark my received messages in this convo as read
      const unreadMine = (data || []).filter(m => m.recipient_vet_id === currentVet.id && !m.read_at).map(m => m.id);
      if (unreadMine.length > 0) {
        const { error: updErr } = await supabase
          .from('messages')
          .update({ read_at: new Date().toISOString() })
          .in('id', unreadMine);
        if (updErr) console.warn('Failed marking read:', updErr);
        await fetchUnreadCounts();
        refreshUnreadCount();
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setMessages([]);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !selectedUserId) return;

    try {
      // Get current user's vet record
      const { data: currentVet, error: currentVetError } = await supabase
        .from('vets')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (currentVetError || !currentVet) {
        console.error('Current user vet not found:', currentVetError);
        showToast('Δεν βρέθηκε το προφίλ σας ως κτηνίατρος', 'error');
        return;
      }

      const { error } = await supabase
        .from('messages')
        .insert({
          sender_vet_id: currentVet.id,
          recipient_vet_id: selectedUserId,
          body: draft.trim(),
        });
      if (error) throw error;
      setDraft('');
      await fetchConversation();
    } catch (err) {
      console.error('Send failed:', err);
      showToast('Σφάλμα αποστολής', 'error');
    }
  };

  const formatUser = (u) => `${u.first_name} ${u.last_name}`;
  const getInitials = (u) => `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase();
  const selectedUser = users.find(u => u.id === selectedUserId);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />

      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 h-[calc(100vh-2rem)] lg:h-screen overflow-hidden">
        {/* Conversations list */}
        <div className="rounded-xl overflow-hidden border flex flex-col h-[300px] lg:h-full shadow-lg" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
          <div className="px-5 py-4 border-b flex-shrink-0" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.65), rgba(31,41,55,0.65))' }}>
            <h3 className="text-white font-bold text-lg">Συνομιλίες</h3>
          </div>
          <div className="divide-y divide-gray-800 flex-1 overflow-y-auto custom-scrollbar">
            {users.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <div className="w-12 h-12 rounded-full mx-auto bg-gray-800 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <p className="font-medium text-gray-400 mb-1">Κανένας χρήστης</p>
                <p className="text-xs">Προσθέστε συναδέλφους από τη σελίδα Εργαζόμενοι.</p>
              </div>
            ) : (
              users.map(u => {
                const isSelected = selectedUserId === u.id;
                return (
                  <button
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-all border-l-4 ${
                      isSelected 
                        ? 'bg-[#1F2937] border-emerald-500' 
                        : 'border-transparent hover:bg-[#1F2937]/50'
                    }`}
                  >
                    <div className="relative">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 shadow-sm"
                        style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                      >
                        {getInitials(u)}
                      </div>
                      {unreadByUserId[u.id] && (
                        <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 border-2 border-[#111827] rounded-full animate-pulse"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`block truncate ${isSelected ? 'text-white font-medium' : 'text-gray-300'}`}>
                        {formatUser(u)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className="rounded-xl overflow-hidden border flex flex-col h-[450px] lg:h-full shadow-lg" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
          <div className="px-6 py-4 border-b flex items-center gap-3 flex-shrink-0" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.65), rgba(31,41,55,0.65))' }}>
            {selectedUser ? (
              <>
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm"
                  style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                >
                  {getInitials(selectedUser)}
                </div>
                <div>
                  <h3 className="text-white font-bold">{formatUser(selectedUser)}</h3>
                </div>
              </>
            ) : (
              <h3 className="text-white font-bold text-lg">Επιλέξτε συνομιλία</h3>
            )}
          </div>
          
          <div className="flex-1 p-4 lg:p-6 space-y-4 overflow-y-auto custom-scrollbar" style={{ backgroundColor: 'rgba(31, 41, 55, 0.3)' }}>
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 opacity-70">
                <svg className="w-16 h-16 mb-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-sm font-medium">Δεν υπάρχουν μηνύματα.</p>
                <p className="text-xs mt-1">Στείλτε το πρώτο μήνυμα για να ξεκινήσετε τη συζήτηση!</p>
              </div>
            ) : (
              messages.map((m, index) => {
                const isFromCurrentUser = m.sender_vet_id === currentVetId;
                
                // Format the date/time nicely
                const dateObj = new Date(m.created_at);
                const isToday = new Date().toDateString() === dateObj.toDateString();
                const timeString = dateObj.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
                const dateString = dateObj.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: '2-digit' });
                const displayTime = isToday ? timeString : `${dateString} • ${timeString}`;

                // Add a small margin-top if the previous message was from a different user
                const prevMessage = messages[index - 1];
                const isFirstInGroup = !prevMessage || prevMessage.sender_vet_id !== m.sender_vet_id;

                return (
                  <div key={m.id} className={`flex flex-col ${isFromCurrentUser ? 'items-end' : 'items-start'} ${isFirstInGroup ? 'mt-4' : 'mt-1'}`}>
                    <div 
                      className={`max-w-[75%] lg:max-w-[65%] px-4 py-2.5 shadow-md relative group ${
                        isFromCurrentUser 
                          ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-2xl rounded-tr-sm' 
                          : 'bg-[#374151] text-gray-100 rounded-2xl rounded-tl-sm border border-gray-600'
                      }`} 
                    >
                      <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                      <div className={`text-[10px] mt-1.5 flex items-center justify-end gap-1 ${isFromCurrentUser ? 'text-emerald-100' : 'text-gray-400'}`}>
                        {displayTime}
                        {isFromCurrentUser && (
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t flex-shrink-0" style={{ borderColor: '#374151', backgroundColor: '#111827' }}>
            <form onSubmit={sendMessage} className="relative flex items-center">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={selectedUser ? "Γράψτε ένα μήνυμα..." : "Επιλέξτε συνομιλία πρώτα..."}
                disabled={!selectedUser}
                className="w-full pl-5 pr-14 py-3.5 bg-[#1F2937] border border-gray-600 rounded-full text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all shadow-inner disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={!draft.trim() || !selectedUser}
                className="absolute right-1.5 p-2.5 rounded-full text-white transition-all hover:scale-105 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                title="Αποστολή"
              >
                <svg className="w-4 h-4 transform translate-x-[-1px] translate-y-[1px]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
          <div
            className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
            }}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${toast.type === 'success' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              <svg className={`w-5 h-5 ${toast.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={toast.type === 'success' ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
              </svg>
            </div>
            <span className="text-white font-medium flex-1">{toast.message}</span>
            <button onClick={() => setToast({ show: false, message: '', type: 'success' })} className="ml-3 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;